"use client";

import { useEffect } from "react";
import posthog from "posthog-js";
import { initGA, pageview } from "@/lib/ga";
import { initMetaPixel } from "@/lib/meta-pixel";
import { persistAttribution } from "@/lib/attribution";

export function AnalyticsProvider({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    // Persist landing UTMs/fbclid so checkout can attach them as cart
    // attributes even after the buyer navigates away from the landing URL
    try {
      if (typeof window !== 'undefined') {
        persistAttribution(window.location.search);
      }
    } catch {}

    // Initialize GA (prod only), manual pageviews
    try {
      initGA();
      if (typeof window !== 'undefined') {
        pageview(window.location.href);
      }
    } catch {}

    // Initialize Meta Pixel (prod only)
    try {
      initMetaPixel();
    } catch {}

    const key = process.env.NEXT_PUBLIC_POSTHOG_KEY || "";
    const host = process.env.NEXT_PUBLIC_POSTHOG_HOST || "https://us.i.posthog.com";
    
    if (typeof window !== "undefined" && key) {
      const w = window as unknown as { __posthogInited?: boolean };
      if (!w.__posthogInited) {
        posthog.init(key, {
          api_host: host,
          person_profiles: "identified_only",
          capture_pageview: false, // We send these manually
          capture_pageleave: true,
          capture_performance: true,
          session_recording: {
            maskAllInputs: true,
          },
          loaded: (posthog) => {
            if (process.env.NODE_ENV === 'development') {
              console.log('PostHog initialized successfully');
            }
          }
        });
        w.__posthogInited = true;

        // First-touch UTM/referrer registration
        try {
          const url = new URL(window.location.href);
          const utms: Record<string, string | undefined> = {
            utm_source: url.searchParams.get('utm_source') || undefined,
            utm_medium: url.searchParams.get('utm_medium') || undefined,
            utm_campaign: url.searchParams.get('utm_campaign') || undefined,
            utm_content: url.searchParams.get('utm_content') || undefined,
            utm_term: url.searchParams.get('utm_term') || undefined,
          };
          const ref = document.referrer || undefined;
          // One-time
          posthog.register_once({
            initial_utm_source: utms.utm_source,
            initial_utm_medium: utms.utm_medium,
            initial_utm_campaign: utms.utm_campaign,
            initial_utm_content: utms.utm_content,
            initial_utm_term: utms.utm_term,
            initial_referrer: ref,
            first_visit_ts: Date.now(),
          });
          // Current session
          posthog.register({
            utm_source: utms.utm_source,
            utm_medium: utms.utm_medium,
            utm_campaign: utms.utm_campaign,
            utm_content: utms.utm_content,
            utm_term: utms.utm_term,
            referrer: ref,
            site_env: process.env.NEXT_PUBLIC_SITE_ENV || 'development',
          });
        } catch {}

        // Initial pageview
        posthog.capture('$pageview');

        if (process.env.NODE_ENV === 'development') {
          console.log('PostHog configured:', {
            key: key.substring(0, 8) + '...',
            host,
            environment: process.env.NEXT_PUBLIC_SITE_ENV || 'development'
          });
        }
      }
    } else if (!key && process.env.NODE_ENV === 'development') {
      console.warn('PostHog key not found. Make sure NEXT_PUBLIC_POSTHOG_KEY is set in your .env.local file');
    }
  }, []);
  
  return <>{children}</>;
}
